<template>
  <!-- "Lisa kontakt" inside a Kontakt field, as "Halda seadmeid" sits in the
       Töökäsk's Seade box: a contact person is a client record, and this makes
       one without leaving the form. The new person lands back in the field;
       the form is saved with its own Salvesta as before. -->
  <v-btn
    size="x-small"
    variant="text"
    color="primary"
    class="contact-add"
    @mousedown.stop
    @click.stop="open()"
  >
    Lisa kontakt
  </v-btn>
  <v-dialog v-model="isVisible" :max-width="plate ? 520 : 560">
    <form v-if="plate" class="dlg" novalidate @submit.prevent="submit()">
      <div class="dlg-head">
        <div>
          <div class="dlg-cap">Kontaktisik</div>
          <h2 class="dlg-title">Lisa kontakt</h2>
        </div>
        <button type="button" class="dlg-x" aria-label="Sulge" @click="isVisible = false"><v-icon>mdi-close</v-icon></button>
      </div>
      <div class="dlg-body">
        <div class="dlg-row2">
          <label>
            <span class="dlg-lbl">Eesnimi <span class="dlg-req">*</span></span>
            <input ref="first" v-model="form.firstName" class="dlg-in" :class="{ bad: tried && !firstName }" :disabled="saving" autocomplete="off">
            <div v-if="tried && !firstName" class="dlg-err">Eesnimi on kohustuslik</div>
          </label>
          <label>
            <span class="dlg-lbl">Perekonnanimi <span class="dlg-req">*</span></span>
            <input v-model="form.lastName" class="dlg-in" :class="{ bad: tried && !lastName }" :disabled="saving" autocomplete="off">
            <div v-if="tried && !lastName" class="dlg-err">Perekonnanimi on kohustuslik</div>
          </label>
        </div>
        <div class="dlg-row2">
          <label>
            <span class="dlg-lbl">Telefon</span>
            <input v-model="form.phone" class="dlg-in" type="tel" :disabled="saving" autocomplete="off">
          </label>
          <label>
            <span class="dlg-lbl">E-post</span>
            <input v-model="form.email" class="dlg-in" :class="{ bad: !emailOk }" type="email" :disabled="saving" autocomplete="off">
            <div v-if="!emailOk" class="dlg-err">E-posti aadress pole õige kujuga</div>
          </label>
        </div>
        <div v-if="matches.length" class="dlg-box" role="status">
          <span><b>Selline klient võib juba olemas olla.</b> Kattub {{ matchedOn }}.</span>
          <div v-for="match in matches" :key="match.id" class="contact-add__who">
            <span><b>{{ match.fullName }}</b><template v-if="match.phone"> · {{ match.phone }}</template><template v-if="match.email"> · {{ match.email }}</template></span>
            <button type="button" class="dlg-link" :disabled="saving" @click="pick(match)">Vali see</button>
          </div>
        </div>
      </div>
      <div class="dlg-foot">
        <span class="dlg-grow">Lisatakse klientide sekka eraisikuna.</span>
        <button type="button" class="dlg-btn dlg-btn--ghost" :disabled="saving" @click="isVisible = false">Loobu</button>
        <button type="submit" class="dlg-btn" :disabled="saving">{{ saving ? 'Salvestan…' : 'Lisa kontakt' }}</button>
      </div>
    </form>
    <v-card v-else>
      <v-toolbar color="primary" theme="dark">
        <v-toolbar-title>Lisa kontakt</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="isVisible = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-form @submit.prevent="submit()">
          <v-text-field v-model="form.firstName" label="Eesnimi" :error-messages="tried && !firstName ? ['Eesnimi on kohustuslik'] : []" :disabled="saving" autofocus />
          <v-text-field v-model="form.lastName" label="Perekonnanimi" :error-messages="tried && !lastName ? ['Perekonnanimi on kohustuslik'] : []" :disabled="saving" />
          <v-text-field v-model="form.phone" label="Telefon" type="tel" :disabled="saving" />
          <v-text-field v-model="form.email" label="E-post" type="email" :error-messages="emailOk ? [] : ['E-posti aadress pole õige kujuga']" :disabled="saving" />
          <v-alert v-if="matches.length" type="warning" variant="tonal" density="compact" class="mb-4">
            <div><b>Selline klient võib juba olemas olla.</b> Kattub {{ matchedOn }}.</div>
            <div v-for="match in matches" :key="match.id" class="contact-add__who">
              <span><b>{{ match.fullName }}</b><template v-if="match.phone"> · {{ match.phone }}</template><template v-if="match.email"> · {{ match.email }}</template></span>
              <v-btn size="small" variant="outlined" :disabled="saving" @click="pick(match)">Vali see</v-btn>
            </div>
          </v-alert>
          <div class="text-caption text-medium-emphasis mb-3">Lisatakse klientide sekka eraisikuna.</div>
          <v-btn type="submit" color="success" :loading="saving">Lisa kontakt</v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api'
import debounce from 'lodash/debounce'
import { mapActions, mapState } from 'vuex'
import '@/styles/dialog-plate.css'

const PRIVATE = 1
const empty = () => ({ firstName: '', lastName: '', phone: '', email: '' })

export default {
  inheritAttrs: false,
  // typed: what the Kontakt box holds — a name being typed is carried over.
  // countryId / cityId: the form's own, so the new person is filed where the
  // object or the firm is.
  props: ['typed', 'countryId', 'cityId'],
  emits: ['created'],
  data () {
    return {
      isVisible: false,
      form: empty(),
      tried: false,
      saving: false,
      matches: [],
      checkNo: 0
    }
  },
  computed: {
    ...mapState(['features']),
    plate () {
      return !!(this.features && this.features.dialogPlate)
    },
    firstName () {
      return (this.form.firstName || '').trim()
    },
    lastName () {
      return (this.form.lastName || '').trim()
    },
    emailOk () {
      const v = (this.form.email || '').trim()
      return !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    },
    matchedOn () {
      const label = { phone: 'telefon', email: 'e-post', name: 'nimi' }
      return [...new Set(this.matches.flatMap(m => m.on))].map(k => label[k]).join(', ')
    },
    checkKey () {
      return JSON.stringify([this.form.firstName, this.form.lastName, this.form.phone, this.form.email])
    }
  },
  watch: {
    checkKey () {
      if (this.isVisible) this.check()
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    open () {
      // The last word is the surname, the rest the given names.
      const words = (typeof this.typed === 'string' ? this.typed : '').trim().split(/\s+/).filter(Boolean)
      this.form = { ...empty(), firstName: words.slice(0, -1).join(' ') || words[0] || '', lastName: words.length > 1 ? words[words.length - 1] : '' }
      this.tried = false
      this.matches = []
      this.isVisible = true
      this.$nextTick(() => this.$refs.first && this.$refs.first.focus())
    },
    // The same question Lisa uus klient asks while it is typed (FormPlate.vue).
    async runCheck () {
      const no = ++this.checkNo
      try {
        const { matches } = await api.checkClient({
          type: PRIVATE,
          firstName: this.firstName || undefined,
          lastName: this.lastName || undefined,
          phone: (this.form.phone || '').trim() || undefined,
          email: (this.form.email || '').trim() || undefined
        })
        if (no === this.checkNo) this.matches = matches
      } catch (err) {
        if (no === this.checkNo) this.matches = []
      }
    },
    // An existing client instead of a second copy of them.
    pick (match) {
      this.$emit('created', { id: match.id, fullName: match.fullName, phone: match.phone, email: match.email })
      this.isVisible = false
    },
    async submit () {
      this.tried = true
      if (!this.firstName || !this.lastName || !this.emailOk || this.saving) return
      this.saving = true
      try {
        const { client } = await api.createClient({
          type: PRIVATE,
          firstName: this.firstName,
          lastName: this.lastName,
          phone: (this.form.phone || '').trim(),
          email: (this.form.email || '').trim(),
          problematic: false,
          countryId: this.countryId || 1,
          cityId: this.cityId || 1
        })
        this.$emit('created', client)
        this.showNotification({ text: 'Kontakt lisatud', color: 'success' })
        this.isVisible = false
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = false
      }
    }
  },
  created () {
    this.check = debounce(this.runCheck, 400)
  }
}
</script>

<style>
/* Not scoped: the dialog is teleported out of the field it was opened from. */
.contact-add { flex: none; }
.contact-add__who { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 6px; overflow-wrap: anywhere; }
</style>
